import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

/**
 * WMAPE and stockout rate per horizon, matching the merchant-level dashboard
 * semantics: WMAPE = sum(abs(forecast total - actual total)) / sum(actual
 * total); stockout rate = stockout days / (horizon days * scored SKUs). Null
 * while the horizon window hasn't fully elapsed or nothing sold.
 */
const horizonAccuracyShape = {
  wmape14d: z.number().nullable(),
  wmape30d: z.number().nullable(),
  wmape60d: z.number().nullable(),
  wmape90d: z.number().nullable(),
  wmape120d: z.number().nullable(),
  stockoutRate14d: z.number().nullable(),
  stockoutRate30d: z.number().nullable(),
  stockoutRate60d: z.number().nullable(),
  stockoutRate90d: z.number().nullable(),
  stockoutRate120d: z.number().nullable(),
};

export const forecastingVolumeGroupValues = ["high", "low"] as const;
export type ForecastingVolumeGroup =
  (typeof forecastingVolumeGroupValues)[number];

export const forecastingAccuracyVolumeGroupSchema = z.object({
  volumeGroup: z.enum(forecastingVolumeGroupValues),
  /** SKUs scored on this origin date that fall in this group */
  skuCount: z.number(),
  ...horizonAccuracyShape,
});

export type ForecastingAccuracyVolumeGroup = z.infer<
  typeof forecastingAccuracyVolumeGroupSchema
>;

export const forecastingAccuracySkuSchema = z.object({
  sku: z.string(),
  /**
   * Low seller per IMSForecastLarge's Syntetos-Boylan classification: average
   * inter-demand interval (days of history / days with sales) >= 1.32,
   * computed over the SKU's daily sales history up to the forecast origin.
   */
  isLowSeller: z.boolean(),
  ...horizonAccuracyShape,
});

export type ForecastingAccuracySku = z.infer<
  typeof forecastingAccuracySkuSchema
>;

export const getForecastingAccuracyMerchantSkus = {
  name: "getForecastingAccuracyMerchantSkus" as const,
  input: z.object({
    /** Forecast run date to score, from the dashboard's available origins */
    originDate: zExt.plainDate(),
    teamId: z.string(),
  }),
  output: z.object({
    /** High sellers first, then low sellers; aggregated over ALL scored SKUs */
    groups: z.array(forecastingAccuracyVolumeGroupSchema),
    /**
     * Per-SKU accuracy, sorted by 90d actual volume descending. Capped, so
     * this may be fewer than the group skuCount totals.
     */
    skus: z.array(forecastingAccuracySkuSchema),
    /** Total scored SKUs before the cap on `skus` */
    totalSkuCount: z.number(),
  }),
};
